import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import DetailsTab from '../components/company/DetailsTab';
import DomainsTab from '../components/company/DomainsTab';
import { UserManagement } from '../components/UserManagement/UserManagement';
import MessagesTab from '../components/company/MessagesTab';
import IntegrationsTab from '../components/company/IntegrationsTab';
import { useTranslation } from 'react-i18next';
import FeatureFlagManagement from '../components/system/FeatureFlagManagement';
import { usePermissions } from '../contexts/PermissionsContext';
import { Company } from '../types/company.types';
import Breadcrumbs from '../components/shared/Breadcrumbs';
import Tabs from '../components/shared/Tabs';

const CompanyManagement = () => {
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const { companies, fetchDomains } = useData();
  const { isSuperAdmin } = usePermissions();

  useEffect(() => {
    if (companyId) {
      fetchDomains(companyId);
    }
  }, [companyId]);

  const company = companies.find((c) => c.id === companyId) as Company | undefined;

  const [activeTab, setActiveTab] = useState('details');

  // Define tabs based on user permissions
  const getTabs = () => {
    const baseTabs = [
      { id: 'details', label: t('company.details') },
      { id: 'domains', label: t('domain.domains') },
      { id: 'users', label: t('user_management.users') },
      { id: 'messages', label: t('company.messages') },
      { id: 'integrations', label: t('company.integrations') },
    ];

    // Only show feature flags tab to super admins
    if (isSuperAdmin()) {
      baseTabs.push({ id: 'feature-flags', label: t('company.feature_flags') });
    }

    return baseTabs;
  };

  const renderTabContent = () => {
    if (!company) {
      return <div className="text-red-500">{t('company.company_not_found')}</div>;
    }

    switch (activeTab) {
      case 'details':
        return <DetailsTab company={company} />;
      case 'users':
        return <UserManagement companyId={companyId} />;
      case 'domains':
        return <DomainsTab />;
      case 'messages':
        return <MessagesTab companyId={companyId || ''} />;
      case 'feature-flags':
        return isSuperAdmin() ? <FeatureFlagManagement companyId={companyId || ''} /> : null;
      case 'integrations':
        return <IntegrationsTab companyId={companyId || ''} />;
      default:
        return <DetailsTab company={company} />;
    }
  };

  return (
    <div>
      {!company ? (
        <div className="text-red-500">{t('company.company_not_found')}</div>
      ) : (
        <>
          <Breadcrumbs
            items={[
              { path: '/companies', label: t('company.companies') },
              { label: company.name },
            ]}
          />

          <Tabs
            tabs={getTabs()}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            className="mb-3"
          />

          <div style={{ height: 'calc(100vh - 155px)' }}>{renderTabContent()}</div>
        </>
      )}
    </div>
  );
};

export default CompanyManagement;
